import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { Header } from 'components/Header';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { Loading } from 'components/Loading';
import { Tabs } from 'components/Tabs';
import { ProfileDetailsTab } from './tabs/ProfileDetailsTab';
import { CalendarsTab } from './tabs/CalendarsTab';
import { ToastType, useToast } from 'services/toast';
import { FailedMessageWithHref } from 'common/ui';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const container = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

type TabOption = {
  label: string;
  value: string;
};

enum ProfileTab {
  DETAILS = 'details',
  CALENDARS = 'calendars'
}

const Profile = () => {
  const user = useRecoilValue(userAtom);
  const [activeTab, setActiveTab] = useState(ProfileTab.DETAILS);
  const organization = useOrganization();
  const hasCalendarIntegration = organization?.hasCalendarIntegration;
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const location = useLocation();

  const isOnboardingPage = location.pathname === Paths.ONBOARDING;

  useEffect(() => {
    const tab = searchParams.get('tab');
    const connectionStatus = searchParams.get('connectionStatus');

    if (hasCalendarIntegration && tab === ProfileTab.CALENDARS) {
      setActiveTab(tab);

      if (connectionStatus === 'success') {
        showToast({
          title: 'Calendar linked successfully',
          type: ToastType.Success
        });
      } else if (connectionStatus === 'failed') {
        showToast({
          title: 'Calendar link failed',
          message: (
            <FailedMessageWithHref
              failedMessage="Try linking calendar again."
              emailSubject="Failed to link calendar"
            />
          ),
          type: ToastType.Error
        });
      }
    }
  }, [hasCalendarIntegration, searchParams]);

  const currentTab = useMemo(() => {
    if (activeTab === ProfileTab.CALENDARS && !isOnboardingPage) {
      return <CalendarsTab />;
    }

    return <ProfileDetailsTab isOnboardingPage={isOnboardingPage} />;
  }, [activeTab, isOnboardingPage]);

  const profileTabs = useMemo(() => {
    const tabs: TabOption[] = [
      {
        label: 'Your details',
        value: ProfileTab.DETAILS
      }
    ];

    if (hasCalendarIntegration) {
      tabs.push({
        label: 'Calendars',
        value: ProfileTab.CALENDARS
      });
    }

    return tabs;
  }, [hasCalendarIntegration]);

  if (!user) {
    return <Loading />;
  }

  return (
    <Box sx={container}>
      <Box>
        <Header
          title="Profile"
          prevPageTitle={isOnboardingPage ? 'SIGNUP' : undefined}
        />
      </Box>

      {!isOnboardingPage && (
        <Box sx={{ marginY: 6 }}>
          <Tabs
            tabs={profileTabs}
            onChange={(value) => {
              setActiveTab(value);
            }}
            value={activeTab}
          />
        </Box>
      )}
      {currentTab}
    </Box>
  );
};

export { Profile };
